import { useDemo } from "@/contexts/DemoContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions, MutationFunction } from "@tanstack/react-query";

/**
 * Hook that returns a guard function to wrap click handlers.
 * If demo account, shows modal and skips the action.
 */
export function useDemoGuard() {
  const { isDemoAccount, guardAction, showDemoModal } = useDemo();
  return { isDemoAccount, guardAction, showDemoModal };
}

/**
 * Drop-in replacement for useMutation that blocks mutations for demo accounts.
 * Only the single demo user (phone +966500000000) is affected.
 */
export function useDemoSafeMutation<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>
) {
  const { isDemoAccount, showDemoModal } = useDemo();
  const queryClient = useQueryClient();

  const originalMutationFn = options.mutationFn as MutationFunction<TData, TVariables> | undefined;

  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    mutationFn: async (...args: Parameters<NonNullable<typeof options.mutationFn>>) => {
      if (isDemoAccount) {
        showDemoModal();
        throw new Error("Demo account - write actions disabled");
      }
      if (!originalMutationFn) {
        throw new Error("No mutationFn provided");
      }
      return (originalMutationFn as (...a: typeof args) => Promise<TData>)(...args);
    },
  }, queryClient);
}
